import * as L from "leaflet";
import React, { useEffect } from "react";
import { Marker, Popup } from "react-leaflet";
import MapPopupController from "../../../../../components/customs/map/MapPopupController";
import MapSurface from "../../../../../components/customs/map/MapSurface";
import MapViewportController from "../../../../../components/customs/map/MapViewportController";
import { getDensityLabel } from "../../MerchantHeatmap.common";
import { LocationData } from "../../MerchantHeatmap.data";
import CustomMapLayer, { HeatPoint } from "./CustomMapLayer";
import "./Heatmap.css";

// Constants for Fairburn, GA
const FAIRBURN_CENTER: [number, number] = [33.5564, -84.5814];
const FAIRBURN_BOUNDARY: [[number, number], [number, number]] = [
    [33.5400, -84.6100], // Southwest
    [33.5900, -84.5500], // Northeast
];
const ZOOM_LEVEL: number = 12.5;

const GREEN_GRADIENT = {
    0.4: "#b9f8cf",
    0.65: "#00c950",
    1.0: "#008236",
} as const;

const ORANGE_GRADIENT = {
    0.4: "#ffd6a7",
    0.65: "#fdb323",
    1.0: "#ff8c00",
} as const;

const RED_GRADIENT = {
    0: "#ffc9c9",
    0.5: "#fb2c36",
    1.0: "#c10007",
} as const;

/** Figma node 21079-3491: numbered circle marker - 35px, #5EC269, 2px white outline. */
const MARKER_SIZE = 35;
const createNumberedCircleIcon = (number: number) => {
    return L.divIcon({
        html: `
            <div style="
                width: ${MARKER_SIZE}px;
                height: ${MARKER_SIZE}px;
                min-width: ${MARKER_SIZE}px;
                min-height: ${MARKER_SIZE}px;
                padding: 3px 8px;
                box-sizing: border-box;
                border-radius: 23.60px;
                background-color: #5EC269;
                outline: 2px white solid;
                outline-offset: -2px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: Nunito, sans-serif;
                font-size: 12px;
                font-weight: 700;
                line-height: 18px;
                color: white;
            ">${number}</div>
        `,
        className: "merchant-heatmap-numbered-marker",
        iconSize: [MARKER_SIZE, MARKER_SIZE],
        iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE / 2],
        popupAnchor: [0, -MARKER_SIZE / 2],
    });
};

interface MerchantHeatMapProps {
    locations: LocationData[];
    selectedLocation: LocationData | null;
    onLocationSelect: (location: LocationData | null) => void;
    borderRadiusPx?: number;
}

const MerchantHeatMap: React.FC<MerchantHeatMapProps> = ({
    locations,
    selectedLocation,
    onLocationSelect,
    borderRadiusPx = 12.13,
}) => {
    const [greenPoints, setGreenPoints] = React.useState<HeatPoint[]>([]);
    const [orangePoints, setOrangePoints] = React.useState<HeatPoint[]>([]);
    const [redPoints, setRedPoints] = React.useState<HeatPoint[]>([]);

    useEffect(() => {
        if (!locations || locations.length === 0) return;

        setGreenPoints(
            locations
                .filter((loc) => loc.redemption_rate > 70)
                .map((loc) => ({
                    lat: loc.latitude,
                    lng: loc.longitude,
                    intensity: loc.redemption_rate / 100,
                }))
        );
        setOrangePoints(
            locations
                .filter((loc) => loc.redemption_rate >= 50 && loc.redemption_rate <= 70)
                .map((loc) => ({
                    lat: loc.latitude,
                    lng: loc.longitude,
                    intensity: loc.redemption_rate / 100,
                }))
        );
        setRedPoints(
            locations
                .filter((loc) => loc.redemption_rate < 50)
                .map((loc) => ({
                    lat: loc.latitude,
                    lng: loc.longitude,
                    intensity: loc.redemption_rate / 100,
                }))
        );
    }, [locations]);

    return (
        <div className="h-full w-full">
            <MapSurface
                initialViewport={{ center: FAIRBURN_CENTER, zoom: ZOOM_LEVEL }}
                style={{ height: "100%", width: "100%", borderRadius: `${borderRadiusPx}px` }}
                maxBounds={FAIRBURN_BOUNDARY}
                maxBoundsViscosity={1.0}
                minZoom={11}
                maxZoom={18}
                className="overflow-hidden"
            >
                <MapViewportController
                    viewport={{ center: FAIRBURN_CENTER, zoom: ZOOM_LEVEL }}
                    transition="set"
                    invalidateSizeDelay={0}
                />
                <MapPopupController selection={selectedLocation} />

                {/* Heatmap xanh lá (High >70%) */}
                <CustomMapLayer
                    points={greenPoints}
                    radius={60}
                    blur={30}
                    max={0.1}
                    gradient={GREEN_GRADIENT}
                />
                {/* Heatmap cam (Medium 50-70%) */}
                <CustomMapLayer
                    points={orangePoints}
                    radius={60}
                    blur={30}
                    max={0.1}
                    gradient={ORANGE_GRADIENT}
                />
                {/* Heatmap đỏ (Low <50%) */}
                <CustomMapLayer
                    points={redPoints}
                    radius={80}
                    blur={50}
                    max={0.1}
                    gradient={RED_GRADIENT}
                />

                {/* Markers: numbered circle (Figma 21079-3491) + popup on click (Figma 21079-3670) */}
                {locations.map((location, index) => (
                    <Marker
                        key={location.id}
                        position={[location.latitude, location.longitude]}
                        icon={createNumberedCircleIcon(location.redemptions)}
                        eventHandlers={{
                            click: () => onLocationSelect(location),
                        }}
                    >
                        <Popup className="merchant-heatmap-popup" closeButton={true}>
                            <div className="merchant-heatmap-popup-content">
                                <div className="merchant-heatmap-popup-name">{location.name}</div>
                                <div className="merchant-heatmap-popup-redemptions">{location.redemptions} redemptions</div>
                                <div className="merchant-heatmap-popup-density">{getDensityLabel(location.redemptions)}</div>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapSurface>
        </div>
    );
};

export default MerchantHeatMap;

